import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Tabs } from 'antd';
import { ProfileCircle, Ranking, Chart21 } from 'iconsax-react';
import TitleText from '../../atoms/TitleText';
import Button from '../../atoms/Button';
import { useNavigate } from 'react-router-dom';

interface ProfileHeaderProps {
  sellerName: string;
  activeTab?: string;
  onTabChange?: (key: string) => void;
  sellerId?: string;
  canSetupMarketPlace?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ sellerName, activeTab = 'overview', onTabChange, sellerId, canSetupMarketPlace }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const items = [
    {
      key: 'overview',
      label: (
        <span className="flex items-center gap-2 text-[16px] font-normal text-[#777777]">
          <ProfileCircle size="18" variant="Bold" />
          {t('sellers.sellerProfile.tabs.overview')}
        </span>
      ),
    },
    {
      key: 'products',
      label: (
        <span className="flex items-center gap-2 text-[16px] font-normal text-[#777777]">
          <Ranking size="18" variant="Bold" />
          {t('sellers.sellerProfile.tabs.products')}
        </span>
      ),
    },
    {
      key: 'statistics',
      label: (
        <span className="flex items-center gap-2 text-[16px] font-normal text-[#777777]">
          <Chart21 size="18" variant="Bold" />
          {t('sellers.sellerProfile.tabs.statistics')}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full" data-testid="profile-header-container">
      {/* Title & Breadcrumb Block - 64px height */}
      <div className="h-[64px] flex flex-col justify-between mb-4">
        <TitleText
          text={t('sellers.sellerProfile.title', { name: sellerName })}
          className="text-[28px] font-bold text-[#090909] dark:text-dark-text-primary leading-none"
        />

        <Breadcrumb
          className="text-[#6B7280] dark:text-[#9CA3AF] text-[16px] font-normal"
          data-testid="profile-breadcrumb"
          items={[
            { title: <span className="cursor-pointer hover:text-blue-500 font-normal" onClick={() => navigate('/')}>{t('sellers.sellerProfile.breadcrumbs.dashboard')}</span> },
            { title: <span className="cursor-pointer hover:text-blue-500 font-normal" onClick={() => navigate('/sellers')}>{t('sellers.sellerProfile.breadcrumbs.sellerList')}</span> },
            { title: <span className="font-normal">{t('sellers.sellerProfile.breadcrumbs.sellerProfile')}</span> },
          ]}
        />
      </div>

      {/* Tabs Block - 46px height with bottom border */}
      <div className="bg-white dark:bg-dark-surface border-b border-[#E7E7E9] dark:border-dark-border h-[46px] px-4 rounded-t-md [&_.ant-tabs-tab:hover]:!text-pink-500 [&_.ant-tabs-tab:hover_.ant-tabs-tab-btn]:!text-pink-500 [&_.ant-tabs-tab:hover_span]:!text-pink-500 [&_.ant-tabs-tab.ant-tabs-tab-active]:!text-pink-500 [&_.ant-tabs-tab.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-pink-500 [&_.ant-tabs-tab.ant-tabs-tab-active_span]:!text-pink-500 [&_.ant-tabs-ink-bar]:!bg-pink-500">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => onTabChange?.(key)}
          items={items}
          data-testid="profile-tabs"
          className="mb-0 custom-tabs -mt-[5px]"
          tabBarGutter={40}
          tabBarExtraContent={
            activeTab === 'products' && (
              <Button
                variant="primary"
                onClick={() => {
                  if (canSetupMarketPlace) {
                    navigate(`/sellers/${sellerId}/profile/setup-marketplace`);
                  } else {
                    navigate(`/sellers/${sellerId}/profile/add-product`);
                  }
                }}
                data-testid="add-product-btn"
              >
                {t('sellers.products.addProduct')}
              </Button>
            )
          }
        />
      </div>
    </div>
  );
};

export default ProfileHeader;
